import { forwardRef, useState, useEffect, useCallback, useImperativeHandle, useRef } from "react";
import { Badge, Banner, BlockStack, Box, Button, ButtonGroup, Card, Divider, FormLayout, Link, Modal, Select, Text, TextField } from "@shopify/polaris";
import { useParams } from "react-router-dom";

import { formatRoundedWeight } from "../../../shared/components/functions";
import { CardHeading } from "../../../shared/components/CardHeading";

const MAX_MULTICOLLO_PARCELS = 20;
const STATE_PROVINCE_REQUIRED_COUNTRY_CODES = new Set(["US"]);

const getEstimatedWeight = (lines) => {
  const relevantLines = Array.isArray(lines) ? lines.filter((line) => Number(line.produktnr) > 50) : [];
  const weight = relevantLines.reduce((totalWeight, line) => totalWeight + (Number(line.Kg) || 0) * (Number(line.aantal) || 0), 0);
  return Math.max(Math.round(weight * 100) / 100, 0.1);
};

const normalizeParcelCount = (value, fallback = 1) => {
  const numericValue = Math.round(Number(value));
  if (!Number.isFinite(numericValue)) return fallback;
  return Math.min(Math.max(numericValue, 1), MAX_MULTICOLLO_PARCELS);
};

const normalizeCountryCode = (value) => String(value || "").trim().toUpperCase();

const getDraftParcels = (draft) => (Array.isArray(draft?.parcels) && draft.parcels.length > 0 ? draft.parcels : []);

const getTotalDraftParcelWeight = (draft) => {
  const totalWeight = getDraftParcels(draft).reduce((weightSum, parcel) => weightSum + (Number(parcel?.weight?.value) || 0), 0);
  return totalWeight > 0 ? totalWeight : 0.1;
};

const splitWeightAcrossParcels = (totalWeight, parcelCount) => {
  const normalizedParcelCount = normalizeParcelCount(parcelCount);
  const totalWeightInGrams = Math.max(Math.round((Number(totalWeight) || 0.1) * 1000), normalizedParcelCount);
  const baseWeightInGrams = Math.floor(totalWeightInGrams / normalizedParcelCount);
  const remainderInGrams = totalWeightInGrams % normalizedParcelCount;

  return Array.from({ length: normalizedParcelCount }, (_, index) => {
    return ((baseWeightInGrams + (index < remainderInGrams ? 1 : 0)) / 1000).toFixed(3);
  });
};

const buildDraftParcelsForCount = (draft, parcelCount) => {
  const currentParcels = getDraftParcels(draft);
  const sourceParcelItems = currentParcels.flatMap((parcel) => (Array.isArray(parcel?.parcel_items) ? parcel.parcel_items : []));
  const weightUnit = currentParcels.find((parcel) => parcel?.weight?.unit)?.weight?.unit || "kg";
  const distributedWeights = splitWeightAcrossParcels(getTotalDraftParcelWeight(draft), parcelCount);

  return Array.from({ length: normalizeParcelCount(parcelCount) }, (_, index) => ({
    weight: {
      value: distributedWeights[index],
      unit: weightUnit,
    },
    parcel_items: index === 0 ? sourceParcelItems : [],
  }));
};

const flattenDraftParcelItems = (draft) =>
  getDraftParcels(draft).flatMap((parcel, parcelIndex) =>
    (Array.isArray(parcel?.parcel_items) ? parcel.parcel_items : []).map((parcelItem, parcelItemIndex) => ({
      ...parcelItem,
      _parcelIndex: parcelIndex,
      _parcelItemIndex: parcelItemIndex,
    }))
  );

const KNOWN_FIELD_PATHS = [
  "order_number",
  "reference",
  "delivery_indicator",
  "to_address.name",
  "to_address.company_name",
  "to_address.email",
  "to_address.phone_number",
  "to_address.address_line_1",
  "to_address.house_number",
  "to_address.address_line_2",
  "to_address.postal_code",
  "to_address.city",
  "to_address.state_province_code",
  "to_address.country_code",
  "parcels.0.weight.value",
  "parcels.0.dimensions.length",
  "parcels.0.dimensions.width",
  "parcels.0.dimensions.height",
  "parcels.0.parcel_items",
];

const SHIPPING_ESTIMATE_MODE_OPTIONS = [
  { label: "Compare our rates and Sendcloud", value: "compare" },
  { label: "Our rate card only", value: "internal" },
  { label: "Sendcloud quote options", value: "sendcloud" },
  { label: "Configured Sendcloud label method", value: "sendcloud_configured" },
];

function formatEstimateMoney(price) {
  if (!price?.value && price?.value !== 0 && price?.value !== "0") return "No price";

  const numericValue = Number(price.value);
  if (!Number.isFinite(numericValue)) return "No price";

  return new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: price.currency || "EUR",
  }).format(numericValue);
}

function formatEstimateLeadTime(hours) {
  const numericHours = Number(hours);
  if (!Number.isFinite(numericHours) || numericHours <= 0) return null;
  if (numericHours < 48) return `${numericHours}h lead time`;
  return `${Math.round(numericHours / 24)} days lead time`;
}

function getEstimateOptions(estimate) {
  return Array.isArray(estimate?.options) ? estimate.options : [];
}

function getPrimaryEstimateOption(estimate) {
  return estimate?.selected_option || getEstimateOptions(estimate).find((option) => option.price_available) || null;
}

function getEstimateOptionSubtitle(option) {
  return [
    option.source_label,
    option.carrier_name,
    option.product_name,
    option.contract_name ? `Contract: ${option.contract_name}` : null,
    formatEstimateLeadTime(option.lead_time_hours),
  ]
    .filter(Boolean)
    .join(" · ");
}

function ShippingEstimateResult({ estimate }) {
  const primaryOption = getPrimaryEstimateOption(estimate);
  const options = getEstimateOptions(estimate);

  if (!estimate) return null;

  if (!primaryOption) {
    return (
      <Banner tone="warning" title="No shipping price could be estimated">
        <p>Try another price source or review the recipient country, postal code and parcel weight.</p>
        {options.slice(0, 3).map((option, index) => (
          <p key={`${option.source}_${option.id || index}`}>{`${option.label}: ${formatEstimateMoney(option.total_price)}`}</p>
        ))}
      </Banner>
    );
  }

  return (
    <BlockStack gap="200">
      {primaryOption && (
        <div style={{ border: "1px solid #dfe3e8", borderRadius: 8, padding: 12 }}>
          <BlockStack gap="100">
            <Text variant="headingMd" as="p">
              {formatEstimateMoney(primaryOption.total_price)}
            </Text>
            <Text fontWeight="semibold" as="p">
              {primaryOption.label}
            </Text>
            <Text tone="subdued" as="p">
              {getEstimateOptionSubtitle(primaryOption)}
            </Text>
          </BlockStack>
        </div>
      )}

      {estimate.customer_charged && (
        <Text tone="subdued" as="p">
          {`Customer was charged ${formatEstimateMoney(estimate.customer_charged.total_price)} for shipping on the source order.`}
        </Text>
      )}

      {options.length > 1 && (
        <BlockStack gap="100">
          <Text variant="headingSm" as="p">
            Other estimate options
          </Text>
          {options
            .filter((option) => option.id !== primaryOption?.id || option.source !== primaryOption?.source)
            .slice(0, 4)
            .map((option, index) => (
              <div
                key={`${option.source}_${option.id || index}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gap: 12,
                  alignItems: "start",
                  borderTop: index === 0 ? "0" : "1px solid #ebebeb",
                  paddingTop: index === 0 ? 0 : 8,
                }}
              >
                <BlockStack gap="100">
                  <Text as="p">{option.label}</Text>
                  <Text tone="subdued" as="p">
                    {getEstimateOptionSubtitle(option)}
                  </Text>
                </BlockStack>
                <Text fontWeight="semibold" as="p">
                  {formatEstimateMoney(option.total_price)}
                </Text>
              </div>
            ))}
        </BlockStack>
      )}

      {Array.isArray(estimate.warnings) &&
        estimate.warnings.map((warning, index) => (
          <Text tone="subdued" as="p" key={`shipping_estimate_warning_${index}`}>
            {warning}
          </Text>
        ))}
    </BlockStack>
  );
}

function getAuthenticatedHeaders() {
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");

  return new Headers({
    Authorization: "Bearer " + currentUser.token,
    "Content-Type": "application/json",
  });
}

async function sendcloudRequest(url, { method = "GET", body } = {}) {
  const response = await fetch(url, {
    method,
    headers: getAuthenticatedHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();

  if (!response.ok) {
    const error = new Error(payload?.message || payload?.error?.message || (typeof payload === "string" ? payload : "Sendcloud request failed."));
    error.details = Array.isArray(payload?.details) ? payload.details : [];
    error.raw = payload?.raw_details || null;
    error.context = payload?.context || null;
    error.sendcloudRequest = payload?.sendcloud_request || null;
    throw error;
  }

  return payload;
}

function normalizeSendcloudError(error) {
  if (!error) return null;

  return {
    message: error.message || "Sendcloud request failed.",
    details: Array.isArray(error.details) ? error.details : [],
    raw: error.raw || null,
    context: error.context || null,
    sendcloudRequest: error.sendcloudRequest || null,
  };
}

function hasLinkedSendcloudId(value) {
  const sendcloudId = String(value || "").trim();
  return Boolean(sendcloudId) && sendcloudId.toLowerCase() !== "null";
}

function base64ToBlob(base64File, mimeType) {
  const byteCharacters = atob(base64File);
  const byteNumbers = Array.from(byteCharacters, (character) => character.charCodeAt(0));
  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
}

function openPrintWindow() {
  const popupWindow = window.open("", "_blank");

  if (popupWindow) {
    popupWindow.document.open();
    popupWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Preparing label</title>
        </head>
        <body style="font-family: sans-serif; padding: 16px;">
          Preparing label...
        </body>
      </html>
    `);
    popupWindow.document.close();
  }

  return popupWindow;
}

function presentPrintableLabel(label, popupWindow, fallbackFilename) {
  if (!label?.file) {
    throw new Error("No printable Sendcloud label was returned.");
  }

  const mimeType = label.mime_type || "application/pdf";
  const labelBlob = base64ToBlob(label.file, mimeType);
  const blobUrl = window.URL.createObjectURL(labelBlob);
  const targetWindow = popupWindow && !popupWindow.closed ? popupWindow : window.open("", "_blank");

  if (!targetWindow) {
    const downloadLink = document.createElement("a");
    downloadLink.href = blobUrl;
    downloadLink.download = fallbackFilename;
    downloadLink.click();
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    return;
  }

  targetWindow.document.open();
  targetWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Sendcloud label</title>
      </head>
      <body style="margin: 0;">
        <iframe id="label-frame" src="${blobUrl}" style="border: 0; width: 100vw; height: 100vh;"></iframe>
        <script>
          const frame = document.getElementById("label-frame");
          frame.addEventListener("load", () => {
            setTimeout(() => {
              try {
                frame.contentWindow.focus();
                frame.contentWindow.print();
              } catch (error) {}
            }, 250);
          });

          window.addEventListener("afterprint", () => {
            setTimeout(() => window.close(), 250);
          });
        </script>
      </body>
    </html>
  `);
  targetWindow.document.close();

  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
}

function cloneDraft(draft) {
  return draft ? JSON.parse(JSON.stringify(draft)) : draft;
}

function updateDraftAtPath(draft, path, value) {
  const nextDraft = cloneDraft(draft);
  if (!nextDraft) return nextDraft;

  let currentLevel = nextDraft;
  for (let index = 0; index < path.length - 1; index += 1) {
    currentLevel = currentLevel[path[index]];
  }
  currentLevel[path[path.length - 1]] = value;
  return nextDraft;
}

function getFieldErrorMap(details) {
  return details.reduce((errorMap, detail) => {
    if (detail?.field && !errorMap[detail.field]) {
      errorMap[detail.field] = detail.message;
    }
    return errorMap;
  }, {});
}

function getUnmappedErrorDetails(details) {
  return details.filter((detail) => {
    if (!detail?.field) return true;
    if (KNOWN_FIELD_PATHS.includes(detail.field)) return false;
    if (/^parcels\.\d+\.weight\.value$/.test(detail.field)) return false;
    if (/^parcels\.\d+\.dimensions\.(length|width|height)$/.test(detail.field)) return false;
    if (/^parcels\.\d+\.parcel_items$/.test(detail.field)) return false;
    if (/^parcels\.\d+\.parcel_items\.\d+\.hs_code$/.test(detail.field)) return false;
    return true;
  });
}

function formatPickupWindow(timeSlots) {
  if (!Array.isArray(timeSlots) || !timeSlots[0]?.start_at || !timeSlots[0]?.end_at) {
    return null;
  }

  const startDate = new Date(timeSlots[0].start_at);
  const endDate = new Date(timeSlots[0].end_at);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }

  const dateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
  const timeFormatter = new Intl.DateTimeFormat(undefined, { timeStyle: "short" });

  return `${dateFormatter.format(startDate)} · ${timeFormatter.format(startDate)} - ${timeFormatter.format(endDate)}`;
}

const ShippingInfoBox = forwardRef(function ShippingInfoBox({ sale, loaded, getSale, setToast, saleIsFullyFulfilled }, ref) {
  const { id } = useParams();
  const containerRef = useRef(null);

  const [sendcloudConfig, setSendcloudConfig] = useState({ test_mode: true });
  const [sendcloudDraft, setSendcloudDraft] = useState(null);
  const [sendcloudShipment, setSendcloudShipment] = useState(null);
  const [sendcloudDataLoaded, setSendcloudDataLoaded] = useState(false);
  const [sendcloudError, setSendcloudError] = useState(null);
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [reprintingLabel, setReprintingLabel] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [ignoredShipmentId, setIgnoredShipmentId] = useState(null);
  const [shippingEstimateMode, setShippingEstimateMode] = useState("compare");
  const [shippingEstimate, setShippingEstimate] = useState(null);
  const [shippingEstimateError, setShippingEstimateError] = useState(null);
  const [estimatingShippingCost, setEstimatingShippingCost] = useState(false);

  const loadSendcloudState = useCallback(async (ignoredShipmentIdOverride) => {
    if (!loaded) return;

    const activeIgnoredShipmentId = ignoredShipmentIdOverride === undefined ? ignoredShipmentId : ignoredShipmentIdOverride;

    setSendcloudDataLoaded(false);

    try {
      const [config, draftResponse, shipmentResponse] = await Promise.all([
        sendcloudRequest("/api/sendcloud/config"),
        sendcloudRequest(`/api/sendcloud/sales-order/${id}/draft`),
        sendcloudRequest(`/api/sendcloud/sales-order/${id}/shipment${activeIgnoredShipmentId ? `?ignore_shipment_id=${encodeURIComponent(activeIgnoredShipmentId)}` : ""}`),
      ]);

      setSendcloudConfig(config);
      setSendcloudDraft(draftResponse?.draft || null);
      setSendcloudShipment(shipmentResponse?.existing ? shipmentResponse : null);
      setSendcloudError(null);
      setShippingEstimate(null);
      setShippingEstimateError(null);
    } catch (error) {
      setSendcloudError(normalizeSendcloudError(error));
      setSendcloudShipment(null);
    } finally {
      setSendcloudDataLoaded(true);
    }
  }, [id, ignoredShipmentId, loaded]);

  useEffect(() => {
    loadSendcloudState();
  }, [loadSendcloudState]);

  async function resetSendcloudID() {
    setSendcloudError(null);
    setSendcloudShipment(null);
    setShippingEstimate(null);
    setShippingEstimateError(null);
    setReviewModalOpen(false);
    try {
      const response = await sendcloudRequest(`/api/sendcloud/sales-order/${id}/reset`, { method: "POST" });
      const nextIgnoredShipmentId = response?.ignored_shipment_id || response?.cancelled_shipment_id || null;
      setIgnoredShipmentId(nextIgnoredShipmentId);
      await getSale();
      await loadSendcloudState(nextIgnoredShipmentId);
      setToast("ok");
    } catch (error) {
      setSendcloudError(normalizeSendcloudError(error));
      setToast("error");
    }
  }

  function handleDraftChange(path) {
    return (value) => {
      setSendcloudError(null);
      setShippingEstimate(null);
      setShippingEstimateError(null);
      setSendcloudDraft((currentDraft) => updateDraftAtPath(currentDraft, path, value));
    };
  }

  function handleParcelCountChange(value) {
    setSendcloudError(null);
    setShippingEstimate(null);
    setShippingEstimateError(null);
    setSendcloudDraft((currentDraft) => {
      if (!currentDraft) return currentDraft;

      return {
        ...cloneDraft(currentDraft),
        parcels: buildDraftParcelsForCount(currentDraft, value),
      };
    });
  }

  function handleShippingEstimateModeChange(value) {
    setShippingEstimateMode(value);
    setShippingEstimate(null);
    setShippingEstimateError(null);
  }

  async function handleEstimateShippingCost() {
    if (!sendcloudDraft) return;

    setEstimatingShippingCost(true);
    setShippingEstimateError(null);

    try {
      const response = await sendcloudRequest(`/api/sendcloud/sales-order/${id}/shipping-estimate`, {
        method: "POST",
        body: { draft: sendcloudDraft, mode: shippingEstimateMode },
      });
      setShippingEstimate(response);
    } catch (error) {
      setShippingEstimate(null);
      setShippingEstimateError(normalizeSendcloudError(error));
    } finally {
      setEstimatingShippingCost(false);
    }
  }

  async function handleCreateOrPrintLabel() {
    if (!saleIsFullyFulfilled || !sendcloudDraft) return;

    const popupWindow = openPrintWindow();
    const createLabelUrl = `/api/sendcloud/sales-order/${id}/create-label${
      ignoredShipmentId ? `?ignore_shipment_id=${encodeURIComponent(ignoredShipmentId)}` : ""
    }`;

    setCreatingLabel(true);
    setSendcloudError(null);

    try {
      const response = await sendcloudRequest(createLabelUrl, {
        method: "POST",
        body: sendcloudDraft,
      });
      const { label, ...shipmentState } = response;
      setSendcloudConfig((currentConfig) => ({ ...currentConfig, test_mode: response.test_mode ?? currentConfig.test_mode }));
      setSendcloudShipment(shipmentState);
      setIgnoredShipmentId(null);
      setReviewModalOpen(false);
      await getSale();
      setToast("ok");
      presentPrintableLabel(label, popupWindow, `sendcloud_label_${sale.order.order_number}.pdf`);
    } catch (error) {
      if (popupWindow && !popupWindow.closed) popupWindow.close();
      const normalizedError = normalizeSendcloudError(error);
      try {
        await getSale();
        await loadSendcloudState();
      } catch (refreshError) {}
      setSendcloudError(normalizedError);
      setToast("error");
    } finally {
      setCreatingLabel(false);
    }
  }

  async function handleReprintLabel() {
    const popupWindow = openPrintWindow();

    setReprintingLabel(true);
    setSendcloudError(null);

    try {
      const response = await sendcloudRequest(`/api/sendcloud/sales-order/${id}/label`);
      presentPrintableLabel(response.label, popupWindow, `sendcloud_label_${sale.order.order_number}.pdf`);
      setToast("ok");
    } catch (error) {
      if (popupWindow && !popupWindow.closed) popupWindow.close();
      setSendcloudError(normalizeSendcloudError(error));
      setReviewModalOpen(true);
      setToast("error");
    } finally {
      setReprintingLabel(false);
    }
  }

  const testModeActive = sendcloudShipment?.test_mode ?? sendcloudConfig?.test_mode ?? true;
  const hasShipment = (Number(sendcloudShipment?.parcel_count) || 0) > 0 || Boolean(sendcloudShipment?.parcel_id);
  const orderHasLinkedSendcloudId = hasLinkedSendcloudId(sale?.order?.sendcloud_id);
  const resetAvailable = orderHasLinkedSendcloudId || hasShipment || Boolean(sendcloudError?.context?.reset_available || sendcloudError?.context?.parcel_id);
  const fieldErrors = getFieldErrorMap(sendcloudError?.details || []);
  const extraErrorDetails = getUnmappedErrorDetails(sendcloudError?.details || []);
  const pickupWindow = formatPickupWindow(sendcloudShipment?.pickup?.time_slots);
  const draftParcels = getDraftParcels(sendcloudDraft);
  const parcelCount = normalizeParcelCount(draftParcels.length || 1);
  const usesOrderPackaging = sendcloudDraft?.meta?.packaging_source === "order";
  const packagingComplete = Boolean(sendcloudDraft?.meta?.packaging_complete);
  const parcelItems = flattenDraftParcelItems(sendcloudDraft);
  const requiresCustomsInformation = Boolean(sendcloudDraft?.meta?.requires_customs_information);
  const missingHsCodeItems = parcelItems.filter((parcelItem) => !(parcelItem?.hs_code || "").trim());
  const customsItemsBlocked = requiresCustomsInformation && (parcelItems.length === 0 || missingHsCodeItems.length > 0);
  const stateProvinceCode = sendcloudDraft?.to_address?.state_province_code || "";
  const stateProvinceCodeRequired = STATE_PROVINCE_REQUIRED_COUNTRY_CODES.has(normalizeCountryCode(sendcloudDraft?.to_address?.country_code));
  const stateProvinceCodeError =
    fieldErrors["to_address.state_province_code"] ||
    (stateProvinceCodeRequired && !stateProvinceCode.trim() ? "State code is required for US addresses." : undefined);
  const recipientStateBlocked = stateProvinceCodeRequired && !stateProvinceCode.trim();
  const totalDraftParcelWeight = getTotalDraftParcelWeight(sendcloudDraft);
  const primaryShippingEstimate = getPrimaryEstimateOption(shippingEstimate);
  const shippingEstimateBasis = `${parcelCount} parcel${parcelCount === 1 ? "" : "s"} · ${formatRoundedWeight(totalDraftParcelWeight)} · ${
    sendcloudDraft?.from_address?.country_code || "?"
  } to ${sendcloudDraft?.to_address?.country_code || "?"}`;

  async function runPrimaryAction() {
    if (!sendcloudDataLoaded) {
      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (hasShipment && !sendcloudError) {
      await handleReprintLabel();
      return;
    }

    setReviewModalOpen(true);
  }

  useImperativeHandle(
    ref,
    () => ({
      runPrimaryAction,
      scrollIntoView: () => setReviewModalOpen(true),
      refresh: () => loadSendcloudState(),
    }),
    [sendcloudDataLoaded, hasShipment, sendcloudError, reprintingLabel, loadSendcloudState]
  );

  const reviewContent = sendcloudDraft && (
    <BlockStack gap="200">
      <BlockStack gap="100">
        <Text variant="headingSm" as="p">
          {`${sendcloudDraft?.meta?.recipient_source_label || "Shipping data"}${testModeActive ? " · test label" : ""}`}
        </Text>
        <Text tone="subdued">
          {testModeActive
            ? "Only editable data is shown here. Check the recipient details below. The email stays local while test mode is active."
            : "Only editable data is shown here. Check the recipient details below before creating the label."}
        </Text>
      </BlockStack>
      {!saleIsFullyFulfilled && (
        <Banner tone="info" title="Shipping quote available before fulfillment">
          <p>You can estimate the shipping cost now. Creating the Sendcloud label stays locked until every required sales line is fulfilled.</p>
        </Banner>
      )}
      {!packagingComplete && (
        <Banner tone="warning" title="Packaging is not ready">
          <p>Complete fulfilment step 3 by adding at least one package with dimensions and weight before creating the label.</p>
        </Banner>
      )}
      {resetAvailable && (
        <Banner tone="warning" title="Sendcloud ID is linked">
          <p>Use reset if the linked shipment or label is stuck, then create the label again from this review screen.</p>
          {sendcloudError?.context?.parcel_id && <p>{`Linked parcel id: ${sendcloudError.context.parcel_id}`}</p>}
        </Banner>
      )}

      {sendcloudError && (
        <Banner tone="critical" title="Sendcloud could not create the label">
          <p>
            {sendcloudError.details.length > 0
              ? "Review the highlighted fields below and correct anything missing or incorrect."
              : sendcloudError.message}
          </p>
          {extraErrorDetails.map((detail, index) => (
            <p key={`${detail.field || "general"}_${index}`}>{`${detail.field ? `${detail.field}: ` : ""}${detail.message}`}</p>
          ))}
        </Banner>
      )}

      <FormLayout>
        <FormLayout.Group condensed>
          <TextField
            label="Order number"
            autoComplete="off"
            value={sendcloudDraft.order_number || ""}
            onChange={handleDraftChange(["order_number"])}
            error={fieldErrors["order_number"]}
          />
          <TextField
            label="Reference"
            autoComplete="off"
            value={sendcloudDraft.reference || ""}
            onChange={handleDraftChange(["reference"])}
            error={fieldErrors["reference"]}
          />
        </FormLayout.Group>
        <TextField
          label="Delivery indicator"
          autoComplete="off"
          value={sendcloudDraft.delivery_indicator || ""}
          onChange={handleDraftChange(["delivery_indicator"])}
          error={fieldErrors["delivery_indicator"]}
        />
      </FormLayout>

      <Divider />

      <BlockStack gap="100">
        <Text variant="headingSm" as="h3">
          Recipient
        </Text>
        <FormLayout>
          <FormLayout.Group condensed>
            <TextField
              label="Contact name"
              autoComplete="off"
              value={sendcloudDraft.to_address?.name || ""}
              onChange={handleDraftChange(["to_address", "name"])}
              error={fieldErrors["to_address.name"]}
            />
            <TextField
              label="Company"
              autoComplete="off"
              value={sendcloudDraft.to_address?.company_name || ""}
              onChange={handleDraftChange(["to_address", "company_name"])}
              error={fieldErrors["to_address.company_name"]}
            />
          </FormLayout.Group>
          <FormLayout.Group condensed>
            <TextField
              label="Email"
              autoComplete="off"
              value={sendcloudDraft.to_address?.email || ""}
              onChange={handleDraftChange(["to_address", "email"])}
              error={fieldErrors["to_address.email"]}
              helpText={testModeActive ? "Not sent to Sendcloud while test mode is active." : undefined}
            />
            <TextField
              label="Phone"
              autoComplete="off"
              value={sendcloudDraft.to_address?.phone_number || ""}
              onChange={handleDraftChange(["to_address", "phone_number"])}
              error={fieldErrors["to_address.phone_number"]}
            />
          </FormLayout.Group>
          <FormLayout.Group condensed>
            <TextField
              label="Street"
              autoComplete="off"
              value={sendcloudDraft.to_address?.address_line_1 || ""}
              onChange={handleDraftChange(["to_address", "address_line_1"])}
              error={fieldErrors["to_address.address_line_1"]}
            />
            <TextField
              label="House number"
              autoComplete="off"
              value={sendcloudDraft.to_address?.house_number || ""}
              onChange={handleDraftChange(["to_address", "house_number"])}
              error={fieldErrors["to_address.house_number"]}
            />
          </FormLayout.Group>
          <TextField
            label="Address line 2"
            autoComplete="off"
            value={sendcloudDraft.to_address?.address_line_2 || ""}
            onChange={handleDraftChange(["to_address", "address_line_2"])}
            error={fieldErrors["to_address.address_line_2"]}
          />
          <FormLayout.Group condensed>
            <TextField
              label="Postal code"
              autoComplete="off"
              value={sendcloudDraft.to_address?.postal_code || ""}
              onChange={handleDraftChange(["to_address", "postal_code"])}
              error={fieldErrors["to_address.postal_code"]}
            />
            <TextField
              label="City"
              autoComplete="off"
              value={sendcloudDraft.to_address?.city || ""}
              onChange={handleDraftChange(["to_address", "city"])}
              error={fieldErrors["to_address.city"]}
            />
          </FormLayout.Group>
          <FormLayout.Group condensed>
            <TextField
              label="State / province code"
              autoComplete="off"
              value={stateProvinceCode}
              onChange={(value) => handleDraftChange(["to_address", "state_province_code"])(value.toUpperCase())}
              error={stateProvinceCodeError}
              helpText={stateProvinceCodeRequired ? "Required for US addresses, for example NC or US-NC." : "Use this when the destination country requires a state or province code."}
            />
            <TextField
              label="Country code"
              autoComplete="off"
              value={sendcloudDraft.to_address?.country_code || ""}
              onChange={handleDraftChange(["to_address", "country_code"])}
              error={fieldErrors["to_address.country_code"]}
              helpText="ISO2 code, for example BE, NL, FR."
            />
          </FormLayout.Group>
        </FormLayout>
      </BlockStack>

      <Divider />

      <BlockStack gap="100">
        <Text variant="headingSm" as="h3">
          Parcel
        </Text>
        <FormLayout>
          <TextField
            label="Number of parcels"
            type="number"
            autoComplete="off"
            value={String(parcelCount)}
            onChange={handleParcelCountChange}
            min={1}
            max={MAX_MULTICOLLO_PARCELS}
            disabled={usesOrderPackaging}
            helpText={usesOrderPackaging ? "Controlled by fulfilment step 3." : `Use 2 or more parcels for Sendcloud multicollo shipments. Maximum: ${MAX_MULTICOLLO_PARCELS}.`}
          />
        </FormLayout>
        <div style={{ display: "grid", gap: "12px" }}>
          {draftParcels.map((parcel, parcelIndex) => (
            <FormLayout key={`parcel_${parcelIndex}`}>
              <TextField
                label={`Parcel ${parcelIndex + 1} weight (kg)`}
                type="number"
                autoComplete="off"
                value={parcel?.weight?.value || ""}
                onChange={handleDraftChange(["parcels", parcelIndex, "weight", "value"])}
                error={fieldErrors[`parcels.${parcelIndex}.weight.value`]}
                step="0.001"
                disabled={usesOrderPackaging}
              />
              {parcel?.dimensions && (
                <Text tone="subdued" as="p">
                  {`${parcel.packaging?.name || "Package"} · ${parcel.dimensions.length} × ${parcel.dimensions.width} × ${parcel.dimensions.height} ${parcel.dimensions.unit || "cm"}`}
                </Text>
              )}
              {fieldErrors[`parcels.${parcelIndex}.parcel_items`] && (
                <Text tone="critical" as="p">
                  {fieldErrors[`parcels.${parcelIndex}.parcel_items`]}
                </Text>
              )}
            </FormLayout>
          ))}
        </div>
        {requiresCustomsInformation && (
          <BlockStack gap="200">
            {parcelCount > 1 && (
              <Text tone="subdued">
                Customs items are previewed once below and will be distributed across parcels automatically when the multicollo shipment is created.
              </Text>
            )}
            <Text tone="subdued">Only sales lines with `ProductID &gt; 50` are included in Sendcloud customs items.</Text>
            {customsItemsBlocked && (
              <Banner tone="critical" title="Customs item data is incomplete">
                <p>
                  {parcelItems.length === 0
                    ? "No sales lines with ProductID > 50 will be sent to Sendcloud for this international shipment."
                    : "One or more parcel items are missing an HS code. Update the product before creating the label."}
                </p>
              </Banner>
            )}
            <div style={{ display: "grid", gap: "12px" }}>
              {parcelItems.map((parcelItem, index) => {
                const hsCodeError = fieldErrors[`parcels.${parcelItem._parcelIndex}.parcel_items.${parcelItem._parcelItemIndex}.hs_code`];
                const hsCodeMissing = !(parcelItem?.hs_code || "").trim();

                return (
                  <div
                    key={`${parcelItem.item_id || "parcel_item"}_${index}`}
                    style={{
                      border: `1px solid ${hsCodeMissing || hsCodeError ? "#d82c0d" : "#dfe3e8"}`,
                      borderRadius: 8,
                      padding: 12,
                    }}
                  >
                    <BlockStack gap="100">
                      <Text variant="bodyMd" fontWeight="semibold" as="p">
                        {parcelItem.description || `Item ${index + 1}`}
                      </Text>
                      <Text tone="subdued" as="p">
                        {`Product ID: ${parcelItem.product_id || "n/a"} · Qty: ${parcelItem.quantity || 0}`}
                      </Text>
                      <Text as="p">{`HS code: ${parcelItem.hs_code || "Missing"}`}</Text>
                      <Text tone="subdued" as="p">{`Origin: ${parcelItem.origin_country || "Missing"}`}</Text>
                      {(hsCodeError || hsCodeMissing) && (
                        <Text tone="critical" as="p">
                          {hsCodeError || "HS code is required for this international shipment."}
                        </Text>
                      )}
                    </BlockStack>
                  </div>
                );
              })}
            </div>
          </BlockStack>
        )}
      </BlockStack>

      <Divider />

      <BlockStack gap="200">
        <BlockStack gap="100">
          <Text variant="headingSm" as="h3">
            Shipping cost estimate
          </Text>
          <Text tone="subdued" as="p">
            {shippingEstimateBasis}
          </Text>
        </BlockStack>
        <FormLayout>
          <Select
            label="Price source"
            value={shippingEstimateMode}
            options={SHIPPING_ESTIMATE_MODE_OPTIONS}
            onChange={handleShippingEstimateModeChange}
          />
        </FormLayout>
        {shippingEstimateError && (
          <Banner tone="critical" title="Shipping cost could not be estimated">
            <p>{shippingEstimateError.message}</p>
          </Banner>
        )}
        <ShippingEstimateResult estimate={shippingEstimate} />
        <Box>
          <Button onClick={handleEstimateShippingCost} loading={estimatingShippingCost} disabled={!sendcloudDraft}>
            Estimate shipping cost
          </Button>
        </Box>
      </BlockStack>
    </BlockStack>
  );
  const reviewModalPrimaryAction = hasShipment
    ? {
        content: (sendcloudShipment?.parcel_count || 1) > 1 ? "Print labels" : "Print label",
        onAction: handleReprintLabel,
        disabled: !sendcloudDraft,
        loading: reprintingLabel,
      }
    : {
        content: testModeActive ? "Create test label" : "Create label",
        onAction: handleCreateOrPrintLabel,
        disabled: !saleIsFullyFulfilled || !sendcloudDraft || !packagingComplete || customsItemsBlocked || recipientStateBlocked,
        loading: creatingLabel,
      };
  const reviewModalSecondaryActions = [
    ...(resetAvailable ? [{ content: "Reset Sendcloud ID", destructive: true, onAction: resetSendcloudID }] : []),
    { content: "Close", onAction: () => setReviewModalOpen(false) },
  ];

  return (
    loaded &&
    sendcloudDataLoaded && (
      <div ref={containerRef} id="sendcloud-shipping-card">
        <Modal
          open={reviewModalOpen}
          onClose={() => setReviewModalOpen(false)}
          title="Check shipping data"
          size="large"
          primaryAction={reviewModalPrimaryAction}
          secondaryActions={reviewModalSecondaryActions}
        >
          <Modal.Section>{reviewContent}</Modal.Section>
        </Modal>

        <Card>
          <CardHeading title="Shipping" />

          <BlockStack gap="400">
            {sale.order.contact.contact_id !== null && (
              <>
                <BlockStack>
                  <CardHeading title="Contact" />
                  <BlockStack>
                    <Link target="_blank" removeUnderline url={`https://admin.schuilenburg.be/contacts/${sale.order.contact.contact_id}`}>
                      {sale.order.contact.contact_name}
                    </Link>
                    <Text>{sale.order.contact.contact_gsm}</Text>
                    <Text>{sale.order.contact.contact_email}</Text>
                  </BlockStack>
                </BlockStack>
                <Divider />
              </>
            )}

            <BlockStack>
              <CardHeading title="Shipping address" />
              {sale.order.delivery_address !== 0 && (
                <BlockStack>
                  <Link target="_blank" removeUnderline url={`https://admin.schuilenburg.be/companies/${sale.order.customer.company_id}`}>
                    {sale.order.delivery_address.company_name}
                  </Link>
                  <Text>{sale.order.delivery_address.company_street_number}</Text>
                  {sale.order.delivery_address.company_zip && sale.order.delivery_address.company_city && (
                    <Text>{`${sale.order.delivery_address.company_zip} ${sale.order.delivery_address.company_city}`}</Text>
                  )}
                  <Text>{sale.order.delivery_address.company_country}</Text>
                </BlockStack>
              )}
            </BlockStack>

            <Divider />

            <BlockStack>
              <CardHeading title="Weight" />
              {sale.lines !== 0 && <Text>{`Estimated weight: ${formatRoundedWeight(getEstimatedWeight(sale.lines))}`}</Text>}
            </BlockStack>

            <Divider />

            <BlockStack gap="300">
              <CardHeading title="Shipping via Sendcloud" />

              <BlockStack gap="200">
                <Box>
                  <Badge tone={testModeActive ? "attention" : "success"}>{testModeActive ? "Test mode" : "Live mode"}</Badge>
                </Box>
                {testModeActive && (
                  <Text tone="subdued">
                    The label is created in test mode. Recipient email stays editable in the form, but it is not sent to Sendcloud while test mode is active, so tracking emails are not triggered.
                  </Text>
                )}
              </BlockStack>

              {hasShipment ? (
                <BlockStack gap="200">
                  <Text>
                    {`A Sendcloud ${testModeActive ? "test " : ""}label is ready${sendcloudShipment?.carrier?.code ? ` with ${sendcloudShipment.carrier.code}` : ""}.`}
                  </Text>
                  <Text>
                    Status: <Badge tone="attention">{sendcloudShipment?.status?.message || "Ready to send"}</Badge>
                  </Text>
                  <Text>{`Parcels: ${sendcloudShipment?.parcel_count || sendcloudShipment?.parcels?.length || 1}`}</Text>
                  <Text>{`Tracking number: ${sendcloudShipment?.tracking_number || "not available yet"}`}</Text>
                  {Array.isArray(sendcloudShipment?.tracking_numbers) && sendcloudShipment.tracking_numbers.length > 1 && (
                    <Text tone="subdued">{`All tracking numbers: ${sendcloudShipment.tracking_numbers.join(", ")}`}</Text>
                  )}

                  {sendcloudShipment?.pickup?.created && (
                    <Banner tone="success" title="DHL pickup requested automatically">
                      <p>{`Pickup${sendcloudShipment.pickup.pickup_id ? ` #${sendcloudShipment.pickup.pickup_id}` : ""} was created in Sendcloud.`}</p>
                      {pickupWindow && <p>{`Pickup window: ${pickupWindow}`}</p>}
                    </Banner>
                  )}

                  {sendcloudShipment?.pickup?.error && (
                    <Banner tone="critical" title="DHL pickup was not booked automatically">
                      <p>{sendcloudShipment.pickup.error.message}</p>
                      {pickupWindow && <p>{`Configured pickup window: ${pickupWindow}`}</p>}
                      {(sendcloudShipment.pickup.error.details || []).map((detail, index) => (
                        <p key={`${detail.field || "pickup"}_${index}`}>{`${detail.field ? `${detail.field}: ` : ""}${detail.message}`}</p>
                      ))}
                    </Banner>
                  )}

                  {sendcloudError && (
                    <Banner tone="critical" title="Sendcloud reported an issue">
                      <p>{sendcloudError.message}</p>
                      {extraErrorDetails.map((detail, index) => (
                        <p key={`${detail.field || "general"}_${index}`}>{`${detail.field ? `${detail.field}: ` : ""}${detail.message}`}</p>
                      ))}
                    </Banner>
                  )}

                  <ButtonGroup>
                    <Button onClick={handleReprintLabel} variant="primary" loading={reprintingLabel}>
                      {(sendcloudShipment?.parcel_count || 1) > 1 ? "Print labels again" : "Print label again"}
                    </Button>
                    {sendcloudShipment?.tracking_url && (
                      <Button external url={sendcloudShipment.tracking_url}>
                        Open tracking
                      </Button>
                    )}
                    <Button tone="critical" onClick={resetSendcloudID}>
                      Reset Sendcloud ID
                    </Button>
                  </ButtonGroup>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  <Text>
                    {saleIsFullyFulfilled
                      ? `Open the Sendcloud review popup to confirm or adjust the shipping data before creating the ${testModeActive ? "test " : ""}label.`
                      : "Open the Sendcloud review popup to estimate the shipping cost now. Label creation unlocks after fulfillment."}
                  </Text>
                  <Text tone="subdued">
                    {`Recipient source: ${sendcloudDraft?.meta?.recipient_source_label || "Unknown"}`}
                  </Text>
                  {primaryShippingEstimate && (
                    <Text tone="subdued">
                      {`Latest shipping estimate: ${formatEstimateMoney(primaryShippingEstimate.total_price)} (${primaryShippingEstimate.source_label}).`}
                    </Text>
                  )}

                  {sendcloudError && (
                    <Banner tone="critical" title="Sendcloud could not create the label">
                      <p>{sendcloudError.message}</p>
                    </Banner>
                  )}

                  <ButtonGroup>
                    <Button onClick={() => setReviewModalOpen(true)} variant="primary" disabled={!sendcloudDraft}>
                      {saleIsFullyFulfilled ? (testModeActive ? "Review test label data" : "Review shipping data") : "Get shipping quote"}
                    </Button>
                    {resetAvailable && (
                      <Button tone="critical" onClick={resetSendcloudID}>
                        Reset Sendcloud ID
                      </Button>
                    )}
                  </ButtonGroup>
                </BlockStack>
              )}
            </BlockStack>
          </BlockStack>
        </Card>
      </div>
    )
  );
});

export default ShippingInfoBox;
